import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { formatNumber } from '../api';
import type { ChartBar, ChartOverlays, ChartThresholds, TraceLevel } from '../types';

/**
 * Daily candles drawn as SVG, in three panes sharing one time axis: price (with
 * optional averages and Bollinger band), volume, and the pattern score each bar
 * closed with against the stream's buy and sell thresholds. Decisions sit on the
 * bar they were made on; selecting one draws what its rule used: the bars each
 * counted signal was read from, the levels those signals rest on, and the stop
 * and entry levels the decision set.
 *
 * Hand-drawn rather than a charting library: recharts has no candlestick, and
 * the overlays here (spans, levels, markers tied to a decision) are the point.
 */

export interface ChartMarker {
  id: number;
  index: number;
  /** Which way the proposal pointed: up for a buy or a cover, down for a sale or a short. */
  side: 'up' | 'down';
  executed: boolean;
  /** Accessible description, also shown on hover. */
  title: string;
}

export interface ChartSpan {
  from: number;
  to: number;
  label: string;
  direction: 'bullish' | 'bearish' | 'neutral';
  levels: Array<{ label: string; price: number }>;
}

export interface ChartHighlight {
  index: number;
  levels: TraceLevel[];
  spans: ChartSpan[];
  fillPrice: number | null;
  /** The score the decision read, which can differ from the close's score on a live bar. */
  tracedScore: number | null;
}

interface CandleChartProps {
  bars: ChartBar[];
  overlays: ChartOverlays;
  showAverages: boolean;
  showBands: boolean;
  thresholds: ChartThresholds | null;
  markers: ChartMarker[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  highlight: ChartHighlight | null;
  provisionalIndex: number | null;
  label: string;
}

const PAD = { top: 24, left: 6, right: 74 };
const PANE = { price: 330, volume: 54, score: 112, gap: 12, axis: 22 };
const HEIGHT = PAD.top + PANE.price + PANE.gap + PANE.volume + PANE.gap + PANE.score + PANE.axis;

function niceTicks(min: number, max: number, count = 6): { ticks: number[]; decimals: number } {
  const span = max - min || Math.abs(max) || 1;
  const rough = span / count;
  const pow = 10 ** Math.floor(Math.log10(rough));
  const step = Number(([1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= rough) ?? 10 * pow).toPrecision(6));
  const ticks: number[] = [];
  for (let value = Math.ceil(min / step) * step; value <= max + step * 1e-9; value += step) {
    ticks.push(Number(value.toPrecision(10)));
  }
  const decimals = Math.min(4, (String(step).split('.')[1] ?? '').length);
  return { ticks, decimals };
}

const shortDate = (date: string): string =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const signed = (value: number, digits = 2): string => `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(digits)}`;

/** Push labels apart vertically so none overlap, keeping their order. */
function spreadLabels<T extends { y: number }>(items: T[], gap: number, min: number, max: number): Array<T & { labelY: number }> {
  const sorted = [...items].sort((a, b) => a.y - b.y).map((item) => ({ ...item, labelY: item.y }));
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = sorted[i - 1];
    const cur = sorted[i];
    if (prev && cur && cur.labelY - prev.labelY < gap) cur.labelY = prev.labelY + gap;
  }
  const last = sorted[sorted.length - 1];
  if (last && last.labelY > max) {
    const shift = last.labelY - max;
    for (const item of sorted) item.labelY = Math.max(min, item.labelY - shift);
  }
  return sorted;
}

export default function CandleChart(props: CandleChartProps) {
  const { bars, overlays, showAverages, showBands, thresholds, markers, selectedId, onSelect, highlight, provisionalIndex, label } = props;
  const wrapRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(960);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const element = wrapRef.current;
    if (!element) return;
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0]?.contentRect.width ?? 0);
      if (next > 0) setWidth(next);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const plotW = Math.max(100, width - PAD.left - PAD.right);
  const step = plotW / Math.max(bars.length, 1);
  const x = (index: number): number => PAD.left + step * (index + 0.5);
  const body = Math.max(1, Math.min(step * 0.66, 14));

  const priceTop = PAD.top;
  const volumeTop = priceTop + PANE.price + PANE.gap;
  const scoreTop = volumeTop + PANE.volume + PANE.gap;
  const axisTop = scoreTop + PANE.score;

  const scale = useMemo(() => {
    let low = Infinity;
    let high = -Infinity;
    for (const bar of bars) {
      low = Math.min(low, bar.low);
      high = Math.max(high, bar.high);
    }
    const consider = (value: number | null | undefined): void => {
      if (value === null || value === undefined || !Number.isFinite(value)) return;
      // A level far outside the bars would squash them; within reach, show it.
      if (value < low * 0.75 || value > high * 1.3) return;
      low = Math.min(low, value);
      high = Math.max(high, value);
    };
    if (showAverages) for (const series of [overlays.sma20, overlays.sma50, overlays.sma200]) series.forEach(consider);
    if (showBands) [...overlays.bbUpper, ...overlays.bbLower].forEach(consider);
    for (const level of highlight?.levels ?? []) consider(level.price);
    for (const span of highlight?.spans ?? []) span.levels.forEach((level) => consider(level.price));
    if (!Number.isFinite(low) || !Number.isFinite(high)) {
      low = 0;
      high = 1;
    }
    const pad = (high - low) * 0.06 || high * 0.02 || 1;
    return { min: low - pad, max: high + pad };
  }, [bars, overlays, showAverages, showBands, highlight]);

  const y = (price: number): number => priceTop + ((scale.max - price) / (scale.max - scale.min)) * PANE.price;
  const yScore = (score: number): number => scoreTop + ((1 - score) / 2) * PANE.score;
  const maxVolume = Math.max(1, ...bars.map((bar) => bar.volume));
  const yVolume = (volume: number): number => volumeTop + PANE.volume - (volume / maxVolume) * PANE.volume;
  const priceTicks = niceTicks(scale.min, scale.max);

  const line = (series: Array<number | null>, map: (value: number) => number): string => {
    let path = '';
    let open = false;
    series.forEach((value, index) => {
      if (value === null || !Number.isFinite(value)) {
        open = false;
        return;
      }
      path += `${open ? 'L' : 'M'}${x(index).toFixed(1)},${map(value).toFixed(1)}`;
      open = true;
    });
    return path;
  };

  // Month labels on the time axis, spaced so they never collide.
  const dateLabels = useMemo(() => {
    const out: Array<{ index: number; text: string }> = [];
    let lastX = -Infinity;
    bars.forEach((bar, index) => {
      const month = bar.date.slice(0, 7);
      const prev = bars[index - 1]?.date.slice(0, 7);
      if (index !== 0 && month === prev) return;
      const at = PAD.left + step * (index + 0.5);
      if (at - lastX < 52) return;
      const date = new Date(`${bar.date}T12:00:00Z`);
      const text = date.toLocaleDateString('en-GB', { month: 'short', ...(date.getUTCMonth() === 0 || index === 0 ? { year: '2-digit' } : {}) });
      out.push({ index, text });
      lastX = at;
    });
    return out;
  }, [bars, step]);

  // Markers stack outward from the bar when several share one.
  const placed = useMemo(() => {
    const stacks = new Map<string, number>();
    return markers
      .filter((marker) => marker.index >= 0 && marker.index < bars.length)
      .map((marker) => {
        const key = `${marker.index}|${marker.side}`;
        const depth = stacks.get(key) ?? 0;
        stacks.set(key, depth + 1);
        // Past a few, markers share the outermost slot rather than leave the pane.
        return { ...marker, depth: Math.min(depth, 4) };
      });
  }, [markers, bars.length]);

  function onMove(event: MouseEvent<SVGRectElement>): void {
    const box = svgRef.current?.getBoundingClientRect();
    if (!box) return;
    const index = Math.floor((event.clientX - box.left - PAD.left) / step);
    setHover(index >= 0 && index < bars.length ? index : null);
  }

  function onMarkerKey(event: KeyboardEvent<SVGGElement>, id: number): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(id);
    }
  }

  const shown = hover ?? (highlight ? highlight.index : bars.length - 1);
  const readout = bars[shown];
  const previous = bars[shown - 1];
  const change = readout && previous && previous.close > 0 ? ((readout.close - previous.close) / previous.close) * 100 : null;

  const inPane = (value: number): boolean => value >= priceTop && value <= priceTop + PANE.price;
  const levelLabels = highlight
    ? spreadLabels(
        highlight.levels.map((level) => ({ ...level, y: y(level.price) })).filter((level) => inPane(level.y)),
        12,
        priceTop + 6,
        priceTop + PANE.price - 2,
      )
    : [];

  // Multi-bar signals are shaded over the bars they read; a one-bar signal is the
  // decision's own bar, already marked by its line.
  const spans = (highlight?.spans ?? []).filter((span) => span.to > span.from).slice(0, 3);
  // Their levels, with near-identical prices merged ("head / first low") and
  // labels spread so none overprint.
  const spanLevels = (() => {
    const merged: Array<{ label: string; price: number; left: number; y: number }> = [];
    for (const span of spans) {
      for (const level of span.levels) {
        const at = y(level.price);
        if (!inPane(at)) continue;
        const twin = merged.find((entry) => Math.abs(entry.y - at) < 2);
        if (twin) {
          if (!twin.label.split(' / ').includes(level.label)) twin.label = `${twin.label} / ${level.label}`;
          twin.left = Math.min(twin.left, x(span.from) - step / 2);
        } else {
          merged.push({ label: level.label, price: level.price, left: x(span.from) - step / 2, y: at });
        }
      }
    }
    return spreadLabels(merged, 11, priceTop + 30, priceTop + PANE.price - 2);
  })();

  return (
    <div ref={wrapRef} className="cc-wrap">
      <svg
        ref={svgRef}
        width={width}
        height={HEIGHT}
        className="cc"
        role="group"
        aria-label={label}
      >
        {/* --- hover readout ------------------------------------------------ */}
        {readout && (
          <text x={PAD.left} y={14} className="cc-readout">
            <tspan className="cc-readout-date">{shortDate(readout.date)}</tspan>
            {provisionalIndex === shown && <tspan className="cc-live"> live</tspan>}
            <tspan dx={10}>O {formatNumber(readout.open, 2)}</tspan>
            <tspan dx={8}>H {formatNumber(readout.high, 2)}</tspan>
            <tspan dx={8}>L {formatNumber(readout.low, 2)}</tspan>
            <tspan dx={8}>C {formatNumber(readout.close, 2)}</tspan>
            {change !== null && (
              <tspan dx={6} className={change >= 0 ? 'cc-pos' : 'cc-neg'}>
                {signed(change)}%
              </tspan>
            )}
            <tspan dx={10}>vol {readout.volume.toLocaleString('en-GB')}</tspan>
            {readout.score !== null && <tspan dx={10}>score {signed(readout.score)}</tspan>}
            {readout.signals.length > 0 && <tspan dx={8} className="cc-dim">{readout.signals.join(', ')}</tspan>}
          </text>
        )}

        {/* --- price grid and axis -------------------------------------- */}
        {priceTicks.ticks.map((tick) => (
          <g key={`p${tick}`}>
            <line x1={PAD.left} x2={PAD.left + plotW} y1={y(tick)} y2={y(tick)} className="cc-grid" />
            <text x={PAD.left + plotW + 6} y={y(tick) + 3} className="cc-axis">
              {formatNumber(tick, priceTicks.decimals)}
            </text>
          </g>
        ))}

        {/* --- what the selected decision's rule read ---------------------- */}
        {spans.map((span, order) => {
          const left = x(span.from) - step / 2;
          const right = x(span.to) + step / 2;
          // A span ending at the right edge is labelled from its right side, so the label stays on the chart.
          const nearEdge = left > PAD.left + plotW - 170;
          return (
            <g key={`span${order}`} className={`cc-span cc-span-${span.direction}`}>
              <rect x={left} y={priceTop} width={Math.max(right - left, 2)} height={PANE.price} />
              <text
                x={nearEdge ? right - 3 : left + 3}
                y={priceTop + 11 + order * 12}
                textAnchor={nearEdge ? 'end' : 'start'}
              >
                {span.label}
              </text>
            </g>
          );
        })}
        {spanLevels.map((level) => (
          <g key={`sl${level.label}${level.price}`} className="cc-span">
            <line x1={level.left} x2={highlight ? x(highlight.index) + step / 2 : level.left} y1={level.y} y2={level.y} className="cc-span-level" />
            <text x={level.left + 3} y={level.labelY - 3} className="cc-span-level-label">
              {level.label} {formatNumber(level.price, 2)}
            </text>
          </g>
        ))}

        {/* --- bands and averages ---------------------------------------- */}
        {showBands && (
          <g className="cc-band">
            <path d={line(overlays.bbUpper, y)} />
            <path d={line(overlays.bbLower, y)} />
          </g>
        )}
        {showAverages && (
          <g>
            <path d={line(overlays.sma20, y)} className="cc-sma20" />
            <path d={line(overlays.sma50, y)} className="cc-sma50" />
            <path d={line(overlays.sma200, y)} className="cc-sma200" />
          </g>
        )}

        {/* --- candles ---------------------------------------------------- */}
        {bars.map((bar, index) => {
          const up = bar.close >= bar.open;
          const top = y(Math.max(bar.open, bar.close));
          const bottom = y(Math.min(bar.open, bar.close));
          const live = provisionalIndex === index;
          return (
            <g key={bar.date} className={`${up ? 'cc-up' : 'cc-down'}${live ? ' cc-provisional' : ''}`}>
              <line x1={x(index)} x2={x(index)} y1={y(bar.high)} y2={y(bar.low)} className="cc-wick" />
              <rect x={x(index) - body / 2} y={top} width={body} height={Math.max(bottom - top, 1)} className="cc-body" />
            </g>
          );
        })}

        {/* --- the selected decision's levels -------------------------------- */}
        {highlight && (
          <g>
            <line
              x1={x(highlight.index)}
              x2={x(highlight.index)}
              y1={priceTop}
              y2={scoreTop + PANE.score}
              className="cc-decision-line"
            />
            {highlight.fillPrice !== null && (
              <line
                x1={x(highlight.index) - step}
                x2={x(highlight.index) + step}
                y1={y(highlight.fillPrice)}
                y2={y(highlight.fillPrice)}
                className="cc-fill"
              />
            )}
            {levelLabels.map((level) => (
              <g key={`${level.label}${level.price}`} className={`cc-level cc-level-${level.kind}`}>
                <line x1={PAD.left} x2={PAD.left + plotW} y1={level.y} y2={level.y} />
                {/* Labels end short of the decision's marker when it sits at the right edge. */}
                <text x={Math.min(PAD.left + plotW - 4, x(highlight.index) - 14)} y={level.labelY - 3} textAnchor="end">
                  {level.label} {formatNumber(level.price, 2)}
                </text>
              </g>
            ))}
          </g>
        )}

        {/* --- volume ----------------------------------------------------- */}
        {bars.map((bar, index) => (
          <rect
            key={`v${bar.date}`}
            x={x(index) - body / 2}
            y={yVolume(bar.volume)}
            width={body}
            height={Math.max(volumeTop + PANE.volume - yVolume(bar.volume), 0)}
            className={`cc-volume ${bar.close >= bar.open ? 'cc-up' : 'cc-down'}`}
          />
        ))}
        <text x={PAD.left + plotW + 6} y={volumeTop + 9} className="cc-axis">
          volume
        </text>

        {/* --- score pane ------------------------------------------------- */}
        <rect x={PAD.left} y={scoreTop} width={plotW} height={PANE.score} className="cc-score-bg" />
        <line x1={PAD.left} x2={PAD.left + plotW} y1={yScore(0)} y2={yScore(0)} className="cc-grid-strong" />
        {[1, -1].map((tick) => (
          <text key={`s${tick}`} x={PAD.left + plotW + 6} y={yScore(tick) + (tick > 0 ? 8 : -1)} className="cc-axis">
            {signed(tick, 0)}
          </text>
        ))}
        {thresholds && (
          <g>
            <line x1={PAD.left} x2={PAD.left + plotW} y1={yScore(thresholds.buy)} y2={yScore(thresholds.buy)} className="cc-threshold cc-threshold-buy" />
            <text x={PAD.left + plotW + 6} y={yScore(thresholds.buy) + 3} className="cc-axis cc-pos">
              buy {signed(thresholds.buy)}
            </text>
            <line x1={PAD.left} x2={PAD.left + plotW} y1={yScore(thresholds.sell)} y2={yScore(thresholds.sell)} className="cc-threshold cc-threshold-sell" />
            <text x={PAD.left + plotW + 6} y={yScore(thresholds.sell) + 3} className="cc-axis cc-neg">
              sell {signed(thresholds.sell)}
            </text>
          </g>
        )}
        <path d={line(bars.map((bar) => bar.score), yScore)} className="cc-score" />
        <text x={PAD.left + 3} y={scoreTop + 10} className="cc-axis">
          pattern score at each close
        </text>
        {highlight?.tracedScore !== null && highlight?.tracedScore !== undefined && (
          <g className="cc-traced">
            <path
              d={`M${x(highlight.index)},${yScore(highlight.tracedScore) - 5} l5,5 l-5,5 l-5,-5 Z`}
            />
            {/* Labelled on whichever side has room. */}
            <text
              x={x(highlight.index) > PAD.left + plotW - 130 ? x(highlight.index) - 9 : x(highlight.index) + 9}
              y={yScore(highlight.tracedScore) + (highlight.tracedScore > 0.8 ? 12 : -6)}
              textAnchor={x(highlight.index) > PAD.left + plotW - 130 ? 'end' : 'start'}
            >
              decided at {signed(highlight.tracedScore)}
            </text>
          </g>
        )}

        {/* --- time axis -------------------------------------------------- */}
        {dateLabels.map((entry) => {
          const at = x(entry.index);
          const anchor = at < PAD.left + 24 ? 'start' : at > PAD.left + plotW - 24 ? 'end' : 'middle';
          return (
            <text key={`d${entry.index}`} x={anchor === 'start' ? PAD.left : at} y={axisTop + 14} className="cc-axis" textAnchor={anchor}>
              {entry.text}
            </text>
          );
        })}

        {/* --- hover capture, under the markers so they stay clickable ------- */}
        <rect
          x={PAD.left}
          y={priceTop}
          width={plotW}
          height={axisTop - priceTop}
          className="cc-capture"
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
        />
        {hover !== null && (
          <line x1={x(hover)} x2={x(hover)} y1={priceTop} y2={axisTop} className="cc-crosshair" pointerEvents="none" />
        )}

        {/* --- decisions -------------------------------------------------- */}
        {placed.map((marker) => {
          const bar = bars[marker.index];
          if (!bar) return null;
          const cx = x(marker.index);
          const base = marker.side === 'up' ? y(bar.low) + 9 + marker.depth * 13 : y(bar.high) - 9 - marker.depth * 13;
          const path =
            marker.side === 'up'
              ? `M${cx},${base - 5} L${cx + 5},${base + 4} L${cx - 5},${base + 4} Z`
              : `M${cx},${base + 5} L${cx + 5},${base - 4} L${cx - 5},${base - 4} Z`;
          const selected = marker.id === selectedId;
          return (
            <g
              key={marker.id}
              className={`cc-marker cc-marker-${marker.side}${marker.executed ? ' cc-executed' : ''}${selected ? ' cc-selected' : ''}`}
              role="button"
              tabIndex={0}
              aria-label={marker.title}
              aria-pressed={selected}
              onClick={() => onSelect(marker.id)}
              onKeyDown={(event) => onMarkerKey(event, marker.id)}
            >
              <title>{marker.title}</title>
              {selected && <circle cx={cx} cy={base} r={9} className="cc-ring" />}
              <path d={path} />
              <line
                x1={cx}
                x2={cx}
                y1={scoreTop + PANE.score - 8}
                y2={scoreTop + PANE.score}
                className="cc-tick"
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
}
